import gsap from 'gsap';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
import { removeTrapFocus, trapFocus } from '~/global';
import { observeScrollAnimationTriggerElement } from '~/lib/animations';
import { OKENDO_BASE_URL, OKENDO_STORE_URL } from '~/lib/constants';
import { generateStarRating } from '~/lib/helpers';
import { calculateDateDifference } from '~/utils/calendar-utils';

gsap.registerPlugin(ScrollToPlugin);

class ReviewsCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiperLoader = this.querySelector('swiper-loader');
    this.swiperContainer = this.querySelector('swiper-container');
    this.reviewsList = this.querySelector('.reviews-carousel__reviews');
    this.aggregatedRatingContainer = this.querySelector('.reviews-carousel__aggregated-rating');
    this.isAnimationEnabled = this.getAttribute('data-animation-enabled') === 'true';
    this.productIDs = this.getAttribute('data-product-ids') ? this.getAttribute('data-product-ids').split(',') : [];
  }

  connectedCallback() {
    if (window.accessibilityMode) {
      this.swiperLoader.remove();
      this.renderAccessibleReviews();

      // Add a load more button for accessible reviews
      this.loadMoreButton = document.createElement('button');
      this.loadMoreButton.classList.add('reviews-carousel__load-more-button', 'button', 'button--primary');
      this.loadMoreButton.textContent = 'Load More Reviews';
      this.loadMoreButton.setAttribute('aria-label', 'Load more reviews');
      this.appendChild(this.loadMoreButton);

      this.loadMoreButton.addEventListener('click', async () => {
        this.loadMoreButton.setAttribute('disabled', '');
        this.loadMoreButton.textContent = 'Loading...';

        const nextUrl = this.loadMoreButton.getAttribute('data-next-url');

        const nextReviewsUrl = OKENDO_BASE_URL + nextUrl;
        const reviews = await this.fetchReviews(nextReviewsUrl);
        this.loadMoreReviews(reviews);
      });
    } else {
      this.renderReviews();
    }

    this.renderAggregatedRating();
  }

  async renderAccessibleReviews() {
    let url = OKENDO_STORE_URL + '/reviews?limit=3';

    const reviews = await this.fetchReviews(url);
    reviews.forEach((review) => {
      if (!review?.productImageUrl || review.productHandle?.includes('shipping-protection')) return;

      const reviewBox = this.buildReviewBox(review);
      const reviewItem = document.createElement('div');
      reviewItem.classList.add('reviews-carousel__item');
      reviewItem.appendChild(reviewBox);

      this.reviewsList.appendChild(reviewItem);

      observeScrollAnimationTriggerElement(reviewItem);
    });

    setTimeout(() => this.addReadmoreButton(), 500);
  }

  async renderReviews() {
    let url = OKENDO_STORE_URL + '/reviews';

    if (this.productIDs.length > 0) {
      url += '?limit=250';
    }

    const reviews = await this.fetchReviews(url);
    reviews.forEach((review) => {
      if (!review?.productImageUrl || review.productHandle?.includes('shipping-protection')) return;

      const reviewBox = this.buildReviewBox(review);
      const swiperSlide = document.createElement('swiper-slide');
      swiperSlide.classList.add('reviews-carousel__swiper-slide');
      swiperSlide.appendChild(reviewBox);
      this.swiperContainer.appendChild(swiperSlide);
      observeScrollAnimationTriggerElement(swiperSlide);
    });

    setTimeout(() => this.addReadmoreButton(), 500);

    if (this.swiperLoader) {
      this.swiperLoader.initSwiper();
      this.swiperContainer.swiper.slideNext();

      setTimeout(() => {
        const swiperSlides = Array.from(this.swiperContainer.querySelectorAll('swiper-slide'));
        swiperSlides.forEach((swiperSlide, index) => {
          if (index <= 2) {
            swiperSlide.classList.add('scroll-trigger', 'animate--slide-in');
            swiperSlide.setAttribute('data-cascade', 'true');
            swiperSlide.style.setProperty('--animation-order', index + 1);
            observeScrollAnimationTriggerElement(swiperSlide);
          }
        });
      }, 1000);
    }
  }

  async renderAggregatedRating() {
    const aggregatedRating = await this.fetchAggregatedRating();

    const totalRatingValues = aggregatedRating.reviewRatingValuesTotal;
    const totalReviews = aggregatedRating.reviewCount;
    const averageRating = totalRatingValues / totalReviews;
    const starsToDisplay = Math.ceil(averageRating);
    const averageRatingRounded = Math.round(averageRating * 100) / 100;

    const starsContainer = this.aggregatedRatingContainer.querySelector('.aggegated-rating__stars');
    const totalReviewsCountContainer = this.aggregatedRatingContainer.querySelector(
      '.aggegated-rating__total-reviews-count'
    );
    starsContainer.innerHTML = generateStarRating(starsToDisplay);
    totalReviewsCountContainer.textContent = totalReviews.toLocaleString() + ' Reviews';
    this.aggregatedRatingContainer.appendChild(starsContainer);
    this.aggregatedRatingContainer.appendChild(totalReviewsCountContainer);
  }

  async fetchReviews(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      if (data?.nextUrl && this.loadMoreButton) {
        this.loadMoreButton.setAttribute('data-next-url', data.nextUrl);
      }

      if (this.productIDs.length > 0) {
        return data?.reviews.filter((review) => {
          return this.productIDs.includes(review.productId.replace('shopify-', ''));
        });
      } else {
        return data?.reviews;
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      return [];
    }
  }

  async fetchAggregatedRating() {
    try {
      const response = await fetch(OKENDO_STORE_URL + '/review_aggregate');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data?.reviewAggregate;
    } catch (error) {
      console.error('Failed to fetch aggregated rating:', error);
      return {};
    }
  }

  buildReviewBox(review) {
    const template = document.getElementById('review-box-template');
    const clone = document.importNode(template.content, true);

    const reviewBox = clone.querySelector('.review-box');
    const productImage = reviewBox.querySelector('.review-box__product-image');
    if (productImage) {
      productImage.src = review?.productImageUrl;
      productImage.alt = '';
    }

    const productTitle = reviewBox.querySelector('.review-box__product-title');
    if (productTitle) productTitle.textContent = review?.productName;

    const messageTitle = reviewBox.querySelector('.review-box__message-title');
    if (messageTitle) messageTitle.textContent = review?.title;

    const messageText = reviewBox.querySelector('.review-box__message-text');
    if (messageText) {
      messageText.setAttribute('data-review-message-text', '');
      messageText.setAttribute('data-reviewer-name', review.reviewer?.displayName || '');
      messageText.textContent = review?.body;
    }

    const reviewStars = reviewBox.querySelector('.review-box__review-stars');
    if (reviewStars) reviewStars.innerHTML = generateStarRating(review?.rating);

    const reviewAuthor = reviewBox.querySelector('.review-box__review-author');
    if (reviewAuthor) {
      const authorDisplayName = reviewAuthor.querySelector('.author-display-name');
      const authorVerifiedBadge = reviewAuthor.querySelector('.author-verified-badge');
      if (authorDisplayName) authorDisplayName.textContent = review.reviewer?.displayName;
      if (authorVerifiedBadge && !review.reviewer?.isVerified) {
        authorVerifiedBadge.remove();
      }
    }

    const reviewDate = reviewBox.querySelector('.review-box__review-date');
    if (reviewDate) reviewDate.textContent = calculateDateDifference(review.dateCreated).replace('ute', '');

    return reviewBox;
  }

  addReadmoreButton() {
    this.querySelectorAll('[data-review-message-text]').forEach((messageText) => {
      if (messageText.clientHeight < messageText.scrollHeight) {
        const readMoreButton = document.createElement('button');
        readMoreButton.classList.add('review-box__readmore-button');
        readMoreButton.textContent = 'Read more';

        // Get the reviewer name from the data attribute
        const reviewerName = messageText.getAttribute('data-reviewer-name');

        // Set aria-label with the reviewer's name if available
        if (reviewerName) {
          readMoreButton.setAttribute('aria-label', `Read more of ${reviewerName}'s review`);
        } else {
          readMoreButton.setAttribute('aria-label', 'Read more');
        }

        messageText.after(readMoreButton);

        readMoreButton.addEventListener('click', () => {
          openReadMoreModal(messageText, readMoreButton);
        });
      }
    });

    const createReadMoreModal = (messageText) => {
      const reviewBoxInner = messageText.closest('.review-box__inner');

      const modal = document.createElement('custom-modal-dialog');
      modal.setAttribute('id', 'ReadMoreModal');
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'read-more-modal-title');
      modal.classList.add('read-more-modal');

      modal.innerHTML = `
        <div class="read-more-modal__backdrop">&nbsp;</div>
        <div class="read-more-modal__content">
          <button class="read-more-modal__close-button" modal-close aria-label="Close review modal">
            <span class="visually-hidden">Close review modal</span>
            <svg class="icon icon-close" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M14.9498 5.05009L5.05029 14.9496" stroke="currentColor" stroke-width="0.833333" stroke-linecap="square"></path>
              <path d="M14.9498 14.9495L5.05029 5.05005" stroke="currentColor" stroke-width="0.833333" stroke-linecap="square"></path>
            </svg>
          </button>
        </div>
      `;

      const content = modal.querySelector('.read-more-modal__content');
      content.appendChild(reviewBoxInner.cloneNode(true));

      ['review-box__review-date', 'review-box__readmore-button'].forEach((className) => {
        content.querySelector(`.${className}`).remove();
      });

      return modal;
    }

    const openReadMoreModal = (messageText, readMoreButton) => {
      const currentModal = this.querySelector('.read-more-modal');
      if (currentModal) {
        currentModal.remove();
      }

      const modal = createReadMoreModal(messageText);

      this.appendChild(modal);
      modal.show(readMoreButton);
    };
  }

  async loadMoreReviews(reviews) {
    let $firstReview = null;

    // Create a visually hidden focusable element to move focus to after loading more reviews
    const visuallyHiddenFocusableElement = document.createElement('span');
    visuallyHiddenFocusableElement.setAttribute('tabindex', '-1');
    visuallyHiddenFocusableElement.classList.add('visually-hidden');
    visuallyHiddenFocusableElement.textContent = 'Loaded more reviews';
    this.reviewsList.appendChild(visuallyHiddenFocusableElement);

    reviews.forEach((review, index) => {
      if (!review?.productImageUrl || review.productHandle?.includes('shipping-protection')) return;

      const $reviewItem = this.buildReviewBox(review);
      if (!$reviewItem) return;

      this.reviewsList.appendChild($reviewItem);

      if (this.animationEnabled) {
        $reviewItem.classList.add('scroll-trigger', 'animate--slide-in');
        observeScrollAnimationTriggerElement($reviewItem);
      }

      if (index === 0) {
        $firstReview = $reviewItem;
      }
    });

    if ($firstReview) {
      $firstReview.setAttribute('tabindex', '-1');
      setTimeout(() => $firstReview.focus(), 100);
    }

    this.loadMoreButton.removeAttribute('disabled');
    this.loadMoreButton.textContent = 'Load More';
  }
}

customElements.define('reviews-carousel', ReviewsCarousel);
